import { CalendarClock, ExternalLink, Eye, MessageSquare, ThumbsUp } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/cn'
import { Chip } from '@/components/ui/Chip'
import { formatRelativeDate, formatScheduledLabel, formatScheduledTooltip } from '@/lib/format'
import { preservingChannel } from '@/lib/routing'
import { SortableHead } from './SortableHead'
import type { SortState } from './sort'
import type { UploadRow, UploadSortCol } from './uploadRows'
import styles from '../pages/AnalyticsPage/AnalyticsPage.module.scss'

export type { UploadRow } from './uploadRows'

export interface UploadsTableProps {
  rows: UploadRow[]
  statsLoading: boolean
  /** Index offset so the rank column reflects the global position
   * (e.g. on page 2 of 25, the first row is rank 26). */
  startIdx?: number
  /** With ``onToggleSort`` the ID / metric headers become click-to-sort,
   * cycling like the retention table (none → asc → desc → none). Sorting
   * itself is the caller's job (``sortUploadRows``) — it owns the rows and
   * the pagination. */
  sort?: SortState<UploadSortCol> | null
  onToggleSort?: (col: UploadSortCol) => void
  /** Adds the ranking score column (criteria version on hover). Off for TikTok,
   * whose rows are all external and never get a Prism ranking. */
  showScore?: boolean
  /** The rankings query is still in flight — render "..." instead of "—". */
  scoreLoading?: boolean
}

/**
 * Uploads table — three metric columns (views/likes/comments) that both
 * YouTube and TikTok APIs return today. The "Vídeo" cell links to the
 * Prism project page using ``preservingChannel`` so the channel context
 * survives the click.
 *
 * Platform-specific metrics (YouTube subscribers gained, TikTok shares)
 * land as a future opt-in — keep this table to the universal three so
 * both platforms share one component.
 */
export function UploadsTable({
  rows,
  statsLoading,
  startIdx = 0,
  sort = null,
  onToggleSort,
  showScore = false,
  scoreLoading = false,
}: UploadsTableProps) {
  const rowClass = cn(styles.row, showScore && styles.rowWithScore)
  return (
    <>
      {onToggleSort ? (
        <div className={cn(rowClass, styles.head)}>
          <SortableHead col="id" label="ID" sort={sort} onClick={onToggleSort} align="center" />
          <span>Vídeo</span>
          <SortableHead col="views" label="Views" sort={sort} onClick={onToggleSort} align="right" />
          <SortableHead col="likes" label="Likes" sort={sort} onClick={onToggleSort} align="right" />
          <SortableHead col="comments" label="Comments" sort={sort} onClick={onToggleSort} align="right" />
          {showScore ? <SortableHead col="score" label="Nota" sort={sort} onClick={onToggleSort} align="right" /> : null}
        </div>
      ) : (
        <HeadRow idHeader="#" showScore={showScore} />
      )}
      <div className={styles.list}>
        {rows.map((r, idx) => (
          <div key={`${r.videoId || r.externalId}-${r.language}`} className={rowClass}>
            <span
              className={styles.rank}
              title={r.publishedAt ? `Publicado em ${formatRelativeDate(r.publishedAt)}` : undefined}
            >
              {String(r.seq ?? startIdx + idx + 1).padStart(2, '0')}
            </span>
            <UploadTitleCell row={r} />
            <MetricCell icon={<Eye />} value={r.views} loading={statsLoading} />
            <MetricCell icon={<ThumbsUp />} value={r.likes} loading={statsLoading} />
            <MetricCell icon={<MessageSquare />} value={r.comments} loading={statsLoading} />
            {showScore ? <ScoreCell row={r} loading={scoreLoading} /> : null}
          </div>
        ))}
      </div>
    </>
  )
}

function HeadRow({ idHeader, showScore = false }: { idHeader: string; showScore?: boolean }) {
  return (
    <div className={cn(styles.row, showScore && styles.rowWithScore, styles.head)}>
      <span style={{ textAlign: 'center' }}>{idHeader}</span>
      <span>Vídeo</span>
      <span style={{ textAlign: 'right' }}>Views</span>
      <span style={{ textAlign: 'right' }}>Likes</span>
      <span style={{ textAlign: 'right' }}>Comments</span>
      {showScore ? <span style={{ textAlign: 'right' }}>Nota</span> : null}
    </div>
  )
}

function UploadTitleCell({ row }: { row: UploadRow }) {
  if (row.isExternal && row.externalUrl) {
    return (
      <a
        href={row.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.title}
        title="Publicado fora do Prism — métricas via YouTube Data API. Ações internas (rescan, regen, ranking) indisponíveis."
      >
        {row.thumbnailUrl ? (
          <img
            src={row.thumbnailUrl}
            alt=""
            className={styles.titleThumb}
            loading="lazy"
            aria-hidden
          />
        ) : null}
        <span className={styles.titleText}>{row.title || row.externalId}</span>
        <Chip variant="default" leadingIcon={<ExternalLink style={{ width: 11, height: 11 }} />}>
          Externo
        </Chip>
      </a>
    )
  }
  const scheduled =
    row.scheduledFor && new Date(row.scheduledFor).getTime() > Date.now() ? row.scheduledFor : null
  return (
    <span className={styles.titleCell}>
      <Link
        to="/projects/$projectId"
        params={{ projectId: row.videoId }}
        search={preservingChannel({ lang: row.language })}
        className={styles.title}
      >
        <span className={styles.titleText}>{row.title}</span>
        {scheduled ? (
          <Chip
            variant="info"
            leadingIcon={<CalendarClock style={{ width: 11, height: 11 }} />}
            title={formatScheduledTooltip(scheduled) ?? undefined}
          >
            {formatScheduledLabel(scheduled)}
          </Chip>
        ) : null}
      </Link>
      {row.watchUrl ? (
        <a
          href={row.watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.watchLink}
          aria-label={`Abrir no YouTube: ${row.title}`}
          title="Abrir no YouTube"
        >
          <ExternalLink aria-hidden />
        </a>
      ) : null}
    </span>
  )
}

interface MetricCellProps {
  icon: React.ReactNode
  value: number | null
  loading: boolean
}

function MetricCell({ icon, value, loading }: MetricCellProps) {
  const isMissing = value === null
  return (
    <span className={cn(styles.metric, isMissing && styles.metricMuted)}>
      <span className={styles.metricIcon} aria-hidden>{icon}</span>
      {isMissing ? (loading ? '...' : '—') : formatCount(value)}
    </span>
  )
}

/**
 * Ranking ``final_score``; the ``criteria_version`` it came from shows on
 * hover, since a re-calibration changes the scale — a 0.70 under v6 and one
 * under v11 aren't the same grade. External rows have no Prism project,
 * hence no ranking.
 */
function ScoreCell({ row, loading }: { row: UploadRow; loading: boolean }) {
  const score = row.score ?? null
  if (score === null) {
    return (
      <span
        className={cn(styles.metric, styles.metricMuted)}
        title={row.isExternal ? 'Publicado fora do Prism — sem ranking' : loading ? undefined : 'Ainda sem ranking'}
      >
        {loading && !row.isExternal ? '...' : '—'}
      </span>
    )
  }
  const tone = score >= 0.65 ? styles.scoreGood : score >= 0.4 ? styles.scoreOk : styles.scoreBad
  return (
    <span
      className={styles.metric}
      title={row.criteriaVersion ? `Nota do ranking · critérios ${row.criteriaVersion}` : 'Nota do ranking'}
    >
      <span className={tone}>{score.toFixed(2)}</span>
    </span>
  )
}

export function UploadsTableSkeleton({ idHeader = '#', showScore = false }: { idHeader?: string; showScore?: boolean }) {
  return (
    <>
      <HeadRow idHeader={idHeader} showScore={showScore} />
      <div className={styles.list}>
        {Array.from({ length: 5 }).map((_, idx) => (
          <div key={`sk-${String(idx)}`} className={cn(styles.row, showScore && styles.rowWithScore)} aria-hidden>
            <span className={styles.rank}>—</span>
            <div className={cn(styles.skeletonShimmer, styles.skeletonRowLine)} style={{ width: '70%' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonRowLine)} style={{ width: '60%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonRowLine)} style={{ width: '60%', justifySelf: 'end' }} />
            <div className={cn(styles.skeletonShimmer, styles.skeletonRowLine)} style={{ width: '60%', justifySelf: 'end' }} />
            {showScore ? (
              <div className={cn(styles.skeletonShimmer, styles.skeletonRowLine)} style={{ width: '60%', justifySelf: 'end' }} />
            ) : null}
          </div>
        ))}
      </div>
    </>
  )
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
  return String(n)
}
